import clsx from "clsx";
import {useContext, useEffect, useMemo} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {Globe2, LogOut, Search} from "lucide-react";
import {NavLink, useLocation} from "react-router-dom";
import AuthContext from "../../context/authContext";
import {apiGet} from "../api/client";
import {
    ALL_COUNTRIES_SCOPE,
    canUseGlobalCountryScope,
    resolveAdminCountryScope,
} from "../api/countryScope";
import {translateAdminRole} from "../auth/roles";
import {useTranslation} from "../i18n";
import {translateCountryName} from "../i18n/countryNames";
import {LanguageSelect} from "../i18n/LanguageSelect";
import {getNavigationForCapabilities} from "../navigation/navigation";
import "./AdminShell.css";

const getSectionTitle = (pathname, navigation) => {
    const active = navigation
        .slice()
        .sort((a, b) => b.href.length - a.href.length)
        .find(
            (item) =>
                pathname === item.href ||
                pathname.startsWith(`${item.href}/`) ||
                item.matchPaths?.some((path) => pathname === path || pathname.startsWith(`${path}/`)),
        );

    return active || navigation[0];
};

const isNavigationActive = (pathname, item, isActive) =>
    isActive ||
    pathname === item.href ||
    pathname.startsWith(`${item.href}/`) ||
    item.matchPaths?.some((path) => pathname === path || pathname.startsWith(`${path}/`));

export const AdminShell = ({
    children,
    role,
    capabilities,
    countryScope: requestedCountryScope,
    onCountryScopeChange,
}) => {
    const {t, i18n} = useTranslation();
    const location = useLocation();
    const queryClient = useQueryClient();
    const {logout} = useContext(AuthContext);
    const navigation = getNavigationForCapabilities({
        role,
        modules: capabilities?.modules || [],
        actions: capabilities?.actions || [],
    });
    const activeSection = getSectionTitle(location.pathname, navigation);
    const allowedCountryIds = useMemo(
        () =>
            Array.from(
                new Set(
                    (capabilities?.scope?.country_ids || [])
                        .map((countryId) => String(countryId))
                        .filter((countryId) => /^[1-9]\d*$/.test(countryId)),
                ),
            ),
        [capabilities?.scope?.country_ids],
    );
    const globalCountryScope = canUseGlobalCountryScope(role);
    const homeCountryId = capabilities?.country?.id;
    const countryScope = resolveAdminCountryScope({
        role,
        allowedCountryIds,
        storedScope: requestedCountryScope,
        homeCountryId,
    });
    const countriesQuery = useQuery({
        queryKey: ["admin-countries", capabilities?.user?.id],
        queryFn: () => apiGet("/api/v2/admin/properties/countries/list"),
        enabled: Boolean(capabilities?.user?.id),
        retry: 1,
    });

    const countryOptions = useMemo(() => {
        const byId = new Map();
        (countriesQuery.data || []).forEach((country) => {
            const id = String(country?.id ?? "");
            if (/^[1-9]\d*$/.test(id)) byId.set(id, {id, name: country.name});
        });
        const principalCountryId = String(capabilities?.country?.id ?? "");
        if (/^[1-9]\d*$/.test(principalCountryId) && !byId.has(principalCountryId)) {
            byId.set(principalCountryId, {
                id: principalCountryId,
                name: capabilities?.country?.name || principalCountryId,
            });
        }
        allowedCountryIds.forEach((id) => {
            if (!byId.has(id)) byId.set(id, {id, name: t("shell.country.fallbackName", {id})});
        });
        const countries = Array.from(byId.values()).sort((left, right) =>
            left.name.localeCompare(right.name, i18n.language),
        );
        return globalCountryScope
            ? countries
            : countries.filter((country) => allowedCountryIds.includes(country.id));
    }, [
        allowedCountryIds,
        capabilities?.country?.id,
        capabilities?.country?.name,
        countriesQuery.data,
        globalCountryScope,
        i18n.language,
        t,
    ]);

    useEffect(() => {
        const nextScope = resolveAdminCountryScope({
            role,
            allowedCountryIds,
            storedScope: requestedCountryScope,
            homeCountryId,
        });
        if (nextScope !== null && nextScope !== requestedCountryScope) {
            onCountryScopeChange?.(nextScope);
        }
    }, [allowedCountryIds, homeCountryId, onCountryScopeChange, requestedCountryScope, role]);

    const changeCountryScope = (event) => {
        const nextScope = resolveAdminCountryScope({
            role,
            allowedCountryIds,
            storedScope: event.target.value,
            homeCountryId,
        });
        if (nextScope === null) return;
        void queryClient.cancelQueries();
        queryClient.removeQueries({
            predicate: ({queryKey}) =>
                queryKey[0] !== "admin-principal" && queryKey[0] !== "admin-countries",
        });
        onCountryScopeChange?.(nextScope);
    };

    return (
        <div className="admin-shell">
            <aside className="admin-sidebar">
                <div className="admin-brand">
                    <div className="admin-brand-mark">S</div>
                    <div className="admin-brand-copy">
                        <p className="admin-brand-title">Sorpa Admin</p>
                        <p className="admin-brand-subtitle">{t("shell.brandSubtitle")}</p>
                    </div>
                </div>

                <nav className="admin-nav" aria-label={t("shell.navigationAriaLabel")}>
                    {navigation.map((item) => {
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.key}
                                to={item.href}
                                className={({isActive}) =>
                                    clsx("admin-nav-link", {
                                        "is-active": isNavigationActive(location.pathname, item, isActive),
                                    })
                                }
                            >
                                <Icon className="admin-nav-icon" size={18} strokeWidth={2.4} />
                                <span>
                                    <span className="admin-nav-label">{item.label}</span>
                                    <span className="admin-nav-description">{item.description}</span>
                                </span>
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="admin-sidebar-footer">
                    <div className="admin-role-card">
                        <p className="admin-role-label">{t("shell.currentRole")}</p>
                        <p className="admin-role-value">{role ? translateAdminRole(role, t) : t("shell.roleUndefined")}</p>
                    </div>
                    <button className="admin-logout" type="button" onClick={() => logout?.()}>
                        <LogOut size={16} /> {t("shell.logout")}
                    </button>
                </div>
            </aside>

            <main className="admin-main">
                <header className="admin-command">
                    <div>
                        <p className="admin-command-title">
                            {activeSection?.label || t("nav.overview.label")}
                        </p>
                        <p className="admin-command-subtitle">
                            {activeSection?.description || t("shell.defaultSubtitle")}
                        </p>
                    </div>
                    <div className="admin-command-actions">
                        <div className="admin-command-search">
                            <Search size={14} />
                            <input
                                className="admin-search"
                                placeholder={t("shell.globalSearch")}
                                aria-label={t("shell.globalSearch")}
                                disabled
                            />
                        </div>
                        <label className="admin-country-scope">
                            <Globe2 size={14} />
                            <select
                                className="admin-country-select"
                                value={countryScope || ""}
                                onChange={changeCountryScope}
                                aria-label={t("shell.country.ariaLabel")}
                            >
                                {globalCountryScope ? (
                                    <option value={ALL_COUNTRIES_SCOPE}>
                                        {t("shell.country.allCountries")}
                                    </option>
                                ) : null}
                                {countryOptions.map((country) => (
                                    <option key={country.id} value={country.id}>
                                        {translateCountryName(country.name, t)}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <LanguageSelect />
                    </div>
                </header>

                <section className="admin-content">{children}</section>
            </main>
        </div>
    );
};
